// Styles
import { IconButton, makeStyles, Typography, Paper, Checkbox, CircularProgress, TextField, Tooltip } from '@material-ui/core';
import { unoTheme } from '../../../assets/styles/unoTheme';
import CloseIcon from '@material-ui/icons/Close';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MyTable from '../../utils/MyTable';
import MyAutoComplete from '../../utils/MyAutoComplete';
import UnoButton from '../../utils/UnoButton';
import NewTemporaryTransferModal from '../../modals/NewTemporaryTransferModal';

// Data
import React, { useEffect, useState } from 'react';
import { ClientAPI } from '../../../API/client';
import { UserAPI } from '../../../API/user';
import { TemporaryTransferAPI } from '../../../API/temporary_transfer';
import { useDispatch, useSelector } from 'react-redux';
import allActions from '../../../actions';
import { checkUserIsRole, roles } from '../../utils/user';

import moment from 'moment';
import { getColorSuitability } from '../../../controllers/ClientController';
import { classificationsMap } from '../backOffice/NewClientScreen';

// Consultoria LEMA
const CONSULTING_ID = 1;

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(3),
        borderBottom: 'solid 1px rgba(0, 0, 0, 0.12)',
        backgroundColor: unoTheme.hoverTable,
    },
    leftHeader: {
        display: 'flex',
        flexDirection: 'column',
    },
    fundLabel: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#1C2228',
    },
    accountLabel: {
        fontSize: '14px',
        color: unoTheme.secondaryColor,
    },
    filters: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        padding: theme.spacing(2),
        paddingBottom: theme.spacing(1),

        '& > *:not(:last-child)': {
            marginRight: theme.spacing(2),
        },
    },
    advisorFilter: {
        // ocupa todo o espaço restante da linha de filtros
        flex: 1,
        minWidth: 0,
    },
    searchField: {
        background: 'white',
        width: '260px',
        flexShrink: 0,
    },
    tableContainer: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
    },
    emptyState: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3),
        textAlign: 'center',
        color: unoTheme.secondaryColor,
    },
    classificationChip: {
        borderRadius: '5px',
    },
    scheduledBadge: {
        display: 'inline-block',
        minWidth: '20px',
        padding: '1px 6px',
        borderRadius: '10px',
        fontSize: '12px',
        fontWeight: 500,
        color: '#000000',
        backgroundColor: unoTheme.warningColor,
        cursor: 'default',
    },
    scheduledEmpty: {
        color: unoTheme.secondaryColor,
    },
    bottom: {
        display: 'flex',
        alignItems: 'flex-end',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: theme.spacing(2),

        '& .bottomButtons': {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
        },
    },
    rightDivBackdrop: {
        backgroundColor: 'white',
        position: 'absolute',
        opacity: 0.7,
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',

        '& .progress': {
            color: unoTheme.mainColor,
        },
    },
}));

function TemporaryTransferClients({ content }) {

    const classes = useStyles();
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.currentUser);

    const [clients, setClients] = useState([]);
    const [checked, setChecked] = useState({});
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [openTransferModal, setOpenTransferModal] = useState(false);
    // client_id -> lista de transferências AGENDADA/ATIVA que já comprometem o cliente
    const [scheduledByClient, setScheduledByClient] = useState({});

    // Filtros
    const [search, setSearch] = useState("");
    const [activeAdvisors, setActiveAdvisors] = useState(null);
    const [selectedAdvisor, setSelectedAdvisor] = useState(null);

    // Filtro por nome (client-side)
    const filteredClients = clients.filter((c) =>
        (c.label_name || '').toLowerCase().includes(search.toLowerCase()));

    const selectedCount = Object.values(checked).filter(Boolean).length;
    const allVisibleSelected = filteredClients.length > 0
        && filteredClients.every((c) => checked[c.id]);
    const someVisibleSelected = filteredClients.some((c) => checked[c.id]);

    async function doGetActiveAdvisors() {
        const response = await UserAPI.getActiveAdvisorsAndAdminsByConsulting(CONSULTING_ID);
        if (response.success) {
            setActiveAdvisors(response.body.rows);
        }
    }

    async function doGetScheduledByClient() {
        const response = await TemporaryTransferAPI.listScheduledByClient();
        if (response.success && response.body) {
            const map = {};
            response.body.rows.forEach((row) => {
                if (!map[row.client_id]) {
                    map[row.client_id] = [];
                }
                map[row.client_id].push(row);
            });
            setScheduledByClient(map);
        }
    }

    async function doGetClients(advisor) {
        setOpenBackdrop(true);

        if (advisor && advisor.id) {
            const { success, body, error } = await UserAPI.getAdvisorClients(advisor.id, null);
            if (success && body) {
                // getAdvisorClients traz todos os clientes vinculados; mantém só ativos da consultoria 1.
                // Enriquece cada linha com o consultor de origem (o filtro selecionado), pois esse
                // endpoint não retorna advisor_id/advisor_name.
                setClients(body.rows
                    .filter((c) => c.active && c.consulting_id == CONSULTING_ID)
                    .map((c) => ({ ...c, advisor_id: advisor.id, advisor_name: advisor.name })));
            } else {
                console.error("RESPONSE ERROR: ", error);
            }
        } else {
            const { success, body, error } = await ClientAPI.listAllActiveClients(CONSULTING_ID);
            if (success && body) {
                setClients(body.rows);
            } else {
                console.error("RESPONSE ERROR: ", error);
            }
        }

        setOpenBackdrop(false);
    }

    function handleAdvisorSelected(event, newValue) {
        setSelectedAdvisor(newValue);
        setChecked({}); // limpa seleção ao trocar de consultor (origem única)
        if (newValue && newValue.id) {
            doGetClients(newValue);
        } else {
            setClients([]);
            setSearch(""); // evita filtro residual no campo desabilitado
        }
    }

    function toggleOne(id) {
        setChecked((prev) => ({ ...prev, [id]: !prev[id] }));
    }

    function toggleAll() {
        const newChecked = { ...checked };
        if (allVisibleSelected) {
            filteredClients.forEach((c) => { newChecked[c.id] = false; });
        } else {
            filteredClients.forEach((c) => { newChecked[c.id] = true; });
        }
        setChecked(newChecked);
    }

    const selectedClients = clients.filter((c) => checked[c.id]);

    useEffect(() => {
        doGetActiveAdvisors();
        doGetScheduledByClient();
    }, []);

    return (
        <div className={classes.root}>

            <Paper style={{ marginBottom: '4px' }}>
                <div className={classes.header}>
                    <div className={classes.leftHeader}>
                        <Typography
                            align={'left'}
                            className={classes.fundLabel}>
                            {"Transferência temporária"}
                        </Typography>
                        <Typography
                            align={'left'}
                            className={classes.accountLabel}>
                            {`SELECIONADOS: ${selectedCount} / ${filteredClients.length}`}
                        </Typography>
                    </div>

                    <IconButton onClick={() => dispatch(allActions.rightContentActions.setRightContent(null))}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </Paper>

            <div className={classes.filters}>
                {activeAdvisors ?
                    <div className={classes.advisorFilter}>
                        <MyAutoComplete
                            id={'advisor-filter-temporary-transfer'}
                            disabled={!checkUserIsRole(currentUser, [roles.superadmin, roles.admin])}
                            options={activeAdvisors}
                            autoComplete
                            customWidth={'100%'}
                            value={selectedAdvisor ? selectedAdvisor : null}
                            onChange={(event, newValue) => handleAdvisorSelected(event, newValue)}
                            label={'Consultor'}
                            placeholder={"Selecione o consultor"}
                            getOptionLabel={(option) => option.name.toUpperCase()}
                            getOptionSelected={(option, value) => option.id === value.id}
                            renderOption={(option) => (
                                <React.Fragment key={'autocomplete-' + option.id}>
                                    <span>{option.name.toUpperCase()}</span>
                                </React.Fragment>
                            )}
                        />
                    </div> : null}
                <TextField
                    className={classes.searchField}
                    label="Nome do cliente"
                    variant="outlined"
                    size="small"
                    disabled={!selectedAdvisor}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {selectedAdvisor ?
            <div className={classes.tableContainer}>
                <MyTable
                    stickyHeader
                    nofixed={true}
                    size="small">
                    <colgroup>
                        <col width="8%" />
                        <col width="36%" />
                        <col width="9%" />
                        <col width="11%" />
                        <col width="14%" />
                        <col width="11%" />
                        <col width="11%" />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">
                                <Checkbox
                                    color="primary"
                                    checked={allVisibleSelected}
                                    indeterminate={someVisibleSelected && !allVisibleSelected}
                                    onChange={() => toggleAll()}
                                    inputProps={{ 'aria-label': 'select all clients' }}
                                />
                            </TableCell>
                            <TableCell align="left">RPPS</TableCell>
                            <TableCell align="center">Agend.</TableCell>
                            <TableCell align="center">Class.</TableCell>
                            <TableCell align="center">Suitability</TableCell>
                            <TableCell align="center">Início carteira</TableCell>
                            <TableCell align="center">Fim carteira</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredClients.map((row) => (
                            <TableRow key={'temporary-transfer-client-' + row.id}>
                                <TableCell align="center">
                                    <Checkbox
                                        color="primary"
                                        checked={checked[row.id] === true}
                                        onChange={() => toggleOne(row.id)}
                                        inputProps={{ 'aria-label': 'select client' }}
                                    />
                                </TableCell>
                                <TableCell align="left">{row.label_name}</TableCell>
                                <TableCell align="center">
                                    {scheduledByClient[row.id] && scheduledByClient[row.id].length ?
                                        <Tooltip
                                            title={
                                                <React.Fragment>
                                                    {scheduledByClient[row.id].map((t) => (
                                                        <div key={'scheduled-' + row.id + '-' + t.id}>
                                                            {moment.utc(t.period_start).format('DD/MM/YYYY')
                                                                + ' a '
                                                                + moment.utc(t.period_end).format('DD/MM/YYYY')
                                                                + ' → '
                                                                + (t.destination_name || '-').toUpperCase()
                                                                + ' (' + t.status + ')'}
                                                        </div>
                                                    ))}
                                                </React.Fragment>
                                            }>
                                            <span className={classes.scheduledBadge}>
                                                {scheduledByClient[row.id].length}
                                            </span>
                                        </Tooltip>
                                        :
                                        <span className={classes.scheduledEmpty}>{'—'}</span>
                                    }
                                </TableCell>
                                <TableCell align="center">
                                    <div
                                        className={classes.classificationChip}
                                        style={{ background: classificationsMap[row.classification]?.color || classificationsMap[null].color }}>
                                        {row.classification || '-'}
                                    </div>
                                </TableCell>
                                <TableCell align="center" style={{ color: getColorSuitability(row.suitability) }}>
                                    {row.suitability ? row.suitability : '-'}
                                </TableCell>
                                <TableCell align="center">{moment.utc(row.portfolio_init).format('MM/YYYY')}</TableCell>
                                <TableCell align="center">{row.portfolio_closed ? row.portfolio_closed : '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </MyTable>
            </div>
            :
            <div className={classes.emptyState}>
                <Typography>{"Selecione um consultor para listar os clientes"}</Typography>
            </div>
            }

            <div className={classes.bottom}>
                <div className={'bottomButtons'}>
                    <UnoButton
                        type="primary"
                        disabled={selectedCount === 0}
                        onClick={() => setOpenTransferModal(true)}>
                        {"Continuar"}
                    </UnoButton>
                </div>
            </div>

            <NewTemporaryTransferModal
                open={openTransferModal}
                selectedClients={selectedClients}
                advisors={activeAdvisors || []}
                scheduledByClient={scheduledByClient}
                handleAction={(scheduled) => {
                    setOpenTransferModal(false);
                    if (scheduled) {
                        setChecked({});
                    }
                }}
            />

            {openBackdrop ?
                <div className={classes.rightDivBackdrop}>
                    <CircularProgress className={'progress'} />
                </div>
                : null}

        </div>
    );
}

export default TemporaryTransferClients;
